import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const InstallCtx = createContext(null);

function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIPadOS = ua.includes("Mac") && "ontouchend" in document; // iPadOS 13+
  return isIOS || isIPadOS;
}

export function InstallProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(detectStandalone());
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIOS = detectIOS();
  const canInstall = !installed && (deferred !== null || isIOS);

  const triggerInstall = useCallback(async () => {
    if (installed) return;
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") setInstalled(true);
      } catch {
        /* swallow */
      } finally {
        setDeferred(null);
      }
      return;
    }
    if (isIOS) {
      setShowIOSGuide(true);
    }
  }, [deferred, installed, isIOS]);

  return (
    <InstallCtx.Provider
      value={{
        canInstall,
        installed,
        isIOS,
        triggerInstall,
        showIOSGuide,
        dismissIOSGuide: () => setShowIOSGuide(false),
      }}
    >
      {children}
    </InstallCtx.Provider>
  );
}

export const useInstall = () => useContext(InstallCtx) || {
  canInstall: false,
  installed: false,
  isIOS: false,
  triggerInstall: () => {},
  showIOSGuide: false,
  dismissIOSGuide: () => {},
};
